/**
 * <Overlay> — the reusable portal-backed scrim shell (atom, L0.1).
 *
 * Encapsulates react-native-paper's `<Portal>` (a JS-layer overlay — no native iOS `UIViewController`,
 * so an in-overlay image picker / PHPicker still presents) plus everything every dialog/sheet needs and
 * must not re-hand-roll: a full-screen scrim, a dismiss-on-backdrop `<Pressable>`, Android hardware-back
 * and web-Escape handling, and the `DimensionsContext` re-provide (paper's Portal doesn't forward React
 * context). It owns ONLY the shell — each consumer (`<Modal>`, `<ActionSheet>`, `<ConfirmDialog>`) styles
 * its own panel and passes it as `children`.
 *
 * `placement` sets vertical anchoring: `center` (dialogs) or `bottom` (sheets). The panel is always
 * horizontally centered — full-width bottom sheets simply set `width: "100%"` on their own panel.
 * Backdrop press + back + Escape are all gated by `dismissable`.
 * Optional `withCloseButton` paints a top-right times `<IconButton>` that calls `onRequestClose`
 * (used by media viewers; Modal keeps its own header close). It sits on its own night disc
 * (`closeButtonFace`): it lands on the scrim or on a viewer's light panel, and must read on both.
 *
 * L'entrée et la sortie de toutes les feuilles et de toutes les boîtes sont ici (`overlayPresence`) :
 * le voile entre en fondu ; une feuille (`bottom`) monte du bas, une boîte centrée entre en fondu ; à la
 * fermeture, l'inverse, et l'overlay ne se démonte qu'une fois la sortie finie. Pendant sa sortie, il
 * garde ce qu'il montrait (ses derniers enfants) et ne répond plus au toucher. En mouvement réduit,
 * rien ne bouge. Aucune feuille ne s'anime elle-même.
 */
import React, { useContext, useEffect, useRef } from "react"
import { Animated, Dimensions, Pressable, View } from "react-native"
import { Portal } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { listenEscape } from "../../utils/listenEscape"
import { IconButton } from "../Buttons/IconButton"
import { CLOSE_BUTTON_FILL, CLOSE_BUTTON_INK } from "./closeButtonFace"
import { overlayPanelMotion, useOverlayPresence } from "./overlayPresence"

type Placement = "center" | "bottom"

/**
 * Le liseré du bord haut d'une feuille qui monte du bas (`placement="bottom"`), 1 px dans le jeton
 * `sheet-edge`. Chaque feuille le pose sur son panneau : l'overlay ne dessine pas le panneau.
 */
export const SHEET_EDGE_WIDTH = 1

export type OverlayProps = {
    visible: boolean
    /** Backdrop tap, Android back and web Escape all call this (when `dismissable`). */
    onRequestClose: () => void
    /** Enables backdrop-press + back + Escape dismissal. Defaults to true. */
    dismissable?: boolean
    /**
     * Hide the portal visually without unmounting (keeps form/media state). Used when a camera /
     * Genius Scan flow must present above the JS overlay — same as legacy `display: none` on cameraOn.
     */
    hidden?: boolean
    /** Panel anchoring: `center` (dialog) or `bottom` (sheet). Defaults to `center`. */
    placement?: Placement
    /**
     * Show a top-right times close button that calls `onRequestClose`. Defaults to false —
     * Modal / ActionSheet / ConfirmDialog keep their own dismiss chrome.
     */
    withCloseButton?: boolean
    /**
     * `deep` : le voile redoublé, pour une surface dont ce qui compte est hors de la page (le guidage de
     * la question du navigateur). Il entre et sort en fondu avec le voile, quel que soit `placement`.
     * Défaut `standard`.
     */
    scrim?: "standard" | "deep"
    children: React.ReactNode
    testID?: string
}

export function Overlay(props: OverlayProps) {
    const {
        visible,
        onRequestClose,
        dismissable = true,
        hidden = false,
        placement = "center",
        withCloseButton = false,
        scrim = "standard",
        children,
        testID,
    } = props

    // Read the context here (outside the Portal, where the app provider is in scope); fall back to raw
    // window dims for Storybook / no-provider. Re-provided inside the Portal below.
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const insets = useSafeAreaInsets()
    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const { mounted, closing, progress } = useOverlayPresence(visible, reduceMotion, motion)
    // Pendant la sortie, le panneau garde ce qu'il montrait : l'appelant a pu déjà vider ses données.
    const kept = useRef(children)
    if (visible) kept.current = children

    useEffect(() => {
        // While hidden (camera trip), let the camera own Android back / Escape.
        if (!visible || !dismissable || hidden) return
        return listenEscape(onRequestClose)
    }, [visible, dismissable, hidden, onRequestClose])

    if (!mounted) return null

    return (
        <Portal>
            <DimensionsContext.Provider value={dimensions}>
                <View
                    style={[styles.overlay, hidden && styles.hidden]}
                    pointerEvents={closing ? "none" : "auto"}
                    testID={testID}
                >
                    <Animated.View style={[styles.layer, styles.scrim, { opacity: progress }]} />
                    {scrim === "deep" && <Animated.View style={[styles.layer, styles.scrim, { opacity: progress }]} />}
                    {dismissable && !hidden && !closing && (
                        <Pressable style={styles.layer} onPress={onRequestClose} accessibilityLabel="Fermer" />
                    )}
                    <Animated.View
                        style={[
                            styles.layer,
                            styles.panel(placement),
                            overlayPanelMotion(placement, progress, dimensions.height),
                        ]}
                        pointerEvents="box-none"
                    >
                        {kept.current}
                    </Animated.View>
                    {withCloseButton && !hidden && (
                        <Animated.View
                            style={[styles.closeButton(insets.top), { opacity: progress }]}
                            pointerEvents="box-none"
                        >
                            <IconButton
                                iconName="times"
                                size={44}
                                iconSize={20}
                                color={CLOSE_BUTTON_FILL}
                                iconColor={CLOSE_BUTTON_INK}
                                onPress={onRequestClose}
                                accessibilityLabel="Fermer"
                            />
                        </Animated.View>
                    )}
                </View>
            </DimensionsContext.Provider>
        </Portal>
    )
}

const styles = StyleSheet.create((theme) => ({
    overlay: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        elevation: 1000,
    },
    hidden: {
        display: "none" as const,
    },
    /** Un calque de la taille de l'écran : le voile, le fond qui ferme, le panneau. */
    layer: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    /** Le voile des feuilles et des boîtes : `backdrop` partout, plus sombre chez l'élève. */
    scrim: {
        backgroundColor: theme.colors.scrim,
    },
    /**
     * Le calque du panneau a la taille de l'écran, pour que les largeurs et hauteurs en % des
     * panneaux s'y lisent comme avant ; il laisse passer le toucher hors du panneau, vers le fond.
     * `placement` drives only vertical anchoring; the panel is always centered horizontally.
     */
    panel: (placement: Placement) => ({
        justifyContent: (placement === "bottom" ? "flex-end" : "center") as "flex-end" | "center",
        alignItems: "center" as const,
    }),
    closeButton: (topInset: number) => ({
        position: "absolute" as const,
        top: topInset + theme.spacing["2"],
        right: theme.spacing["3"],
        zIndex: 20,
    }),
}))
